/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Styles
import borders from "assets/theme/base/borders";
import boxShadows from "assets/theme/base/boxShadows";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";

// Soft UI Dashboard PRO React helper functions
import linearGradient from "assets/theme/functions/linearGradient";
import pxToRem from "assets/theme/functions/pxToRem";

const { borderRadius } = borders;
const { fontWeightMedium, fontWeightBold, size } = typography;
const { gradients, transparent, white } = colors.light;
const { buttonBoxShadow } = boxShadows;

function swalStyles(mode) {
  const isDark = mode === "dark";

  return {
    ".swal2-container": {
      zIndex: `${9999} !important`,
    },

    ".swal2-popup": {
      borderRadius: `${borderRadius.xl} !important`,
      ...(isDark && {
        backgroundColor: "#121c1c !important",
        border: "1px solid #1e3a38",
        boxShadow: "0 8px 32px rgba(0,0,0,0.7) !important",
        color: "#f0f4f8 !important",
      }),

      "& .swal2-title": {
        fontWeight: fontWeightMedium,
        ...(isDark && { color: "#f0f4f8 !important" }),
      },

      "& .swal2-html-container": {
        ...(isDark && { color: "rgba(255,255,255,0.65) !important" }),
      },

      // Success icon ring
      "& .swal2-icon.swal2-success": {
        ...(isDark && {
          borderColor: "rgba(165,220,134,0.3) !important",
          color: "#a5dc86 !important",
        }),
        "& .swal2-success-ring": {
          ...(isDark && { border: "4px solid rgba(165,220,134,0.3) !important" }),
        },
        "& [class^=swal2-success-line]": {
          ...(isDark && { backgroundColor: "#a5dc86 !important" }),
        },
      },

      // Error icon
      "& .swal2-icon.swal2-error": {
        ...(isDark && {
          borderColor: "rgba(242,116,116,0.3) !important",
          color: "#f27474 !important",
        }),
        "& [class^=swal2-x-mark-line]": {
          ...(isDark && { backgroundColor: "#f27474 !important" }),
        },
      },

      // Warning icon
      "& .swal2-icon.swal2-warning": {
        ...(isDark && {
          borderColor: "rgba(250,162,86,0.3) !important",
          color: "#faa256 !important",
        }),
      },

      "& .button, & .swal2-confirm": {
        fontSize: `${size.xs} !important`,
        fontWeight: fontWeightBold,
        borderRadius: `${borderRadius.md} !important`,
        padding: `${pxToRem(12)} ${pxToRem(24)}`,
        margin: pxToRem(3.75),
        textAlign: "center",
        textTransform: "uppercase",
        userSelect: "none",
        backgroundSize: "150% !important",
        backgroundPositionX: "25% !important",
        transition: `all 150ms ease-in`,
        backgroundImage: `${linearGradient(
          gradients.success.main,
          gradients.success.state
        )} !important`,
        backgroundColor: transparent.main,
        color: white.main,
        height: pxToRem(40),
        boxShadow: `${buttonBoxShadow.main} !important`,
        border: "none",
        cursor: "pointer",

        "&:hover, &:focus, &:active": {
          backgroundImage: `${linearGradient(
            gradients.success.main,
            gradients.success.state
          )} !important`,
          boxShadow: `${buttonBoxShadow.stateOf} !important`,
        },

        "& .material-icons": {
          fontSize: size.sm,
          marginRight: pxToRem(4),
        },
      },

      "& .button.button-success": {
        backgroundImage: `${linearGradient(
          gradients.success.main,
          gradients.success.state
        )} !important`,

        "&:hover, &:focus, &:active": {
          backgroundImage: `${linearGradient(
            gradients.success.main,
            gradients.success.state
          )} !important`,
        },
      },

      "& .button.button-error": {
        backgroundImage: `${linearGradient(
          gradients.error.main,
          gradients.error.state
        )} !important`,

        "&:hover, &:focus, &:active": {
          backgroundImage: `${linearGradient(
            gradients.error.main,
            gradients.error.state
          )} !important`,
        },
      },

      "& .button-flex": {
        display: "inline-flex !important",
        alignItems: "center",
      },

      "& a, a:visited": {
        color: isDark ? "rgba(255,255,255,0.6)" : "#545454",
        textDecoration: "none",
      },

      "& .swal2-image": {
        borderRadius: borderRadius.xl,
        marginBottom: 0,
      },
    },
  };
}

export default swalStyles;
